/**
 * Normalisation et détection de changement plaque / département.
 *
 * Source unique de vérité pour le format SIV (`AA-123-BB`), partagée entre
 * l'étape 1 (`PlateAndDepartmentScreen`) et l'étape de vérification
 * (`VerifyInfoScreen`). La comparaison normalisée évite de relancer ECG Auto
 * pour une simple différence de casse ou de tirets.
 */

export const REGPLATE_REGEX = /^[A-Z]{2}-?[0-9]{3}-?[A-Z]{2}$/i;

/** Normalise une plaque vers le format SIV `AA-123-BB`. Laisse la saisie brute
 *  (uppercase/trim) si elle ne fait pas 7 caractères alphanumériques. */
export const normalizePlate = (raw: string): string => {
  const upper = raw
    .toUpperCase()
    .trim()
    .replace(/[^A-Z0-9]/g, '');
  if (upper.length !== 7) return raw.toUpperCase().trim();
  return `${upper.slice(0, 2)}-${upper.slice(2, 5)}-${upper.slice(5, 7)}`;
};

/** Ne garde que les chiffres d'un département (2 ou 3). */
export const normalizeDept = (raw: string): string => raw.trim().replace(/[^0-9]/g, '');

export interface PlateDeptChange {
  plateChanged: boolean;
  deptChanged: boolean;
  /** `true` si la plaque OU le département a changé → recalcul ECG Auto requis. */
  changed: boolean;
  /** Plaque normalisée prête pour l'appel ECG. */
  normalizedPlate: string;
  /** Département normalisé prêt pour l'appel ECG. */
  normalizedDept: string;
}

/**
 * Compare la plaque/département de référence (saisis à l'étape 1) avec ceux
 * issus de l'étape de vérification, après normalisation, pour décider d'un
 * recalcul ECG Auto.
 */
export const detectPlateDeptChange = (
  originalPlate: string,
  originalDept: string,
  nextPlate: string,
  nextDept: string,
): PlateDeptChange => {
  const normalizedPlate = normalizePlate(nextPlate);
  const normalizedDept = normalizeDept(nextDept);
  const plateChanged = normalizedPlate !== normalizePlate(originalPlate);
  const deptChanged = normalizedDept !== normalizeDept(originalDept);
  return {
    plateChanged,
    deptChanged,
    changed: plateChanged || deptChanged,
    normalizedPlate,
    normalizedDept,
  };
};
